import React, { useEffect, useState, Suspense } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { addCartItem } from '../../redux/cart/cartActions';
import { setLoading } from '../../redux/loader/loaderActions';
import { toast } from 'react-toastify';
import { getBook } from '../../api';
import {
    Container, Grid, Paper, Title, Text, Button, Group, Stack, Badge,
    Image, createStyles, Loader, Divider, ActionIcon, Box
} from '@mantine/core';
import { IconShoppingCart, IconArrowLeft, IconCheck } from '@tabler/icons-react';
import ReviewSection from '../../components/ReviewSection/ReviewSection';

// 3D Dependencies
import { Canvas, useLoader } from '@react-three/fiber';
import { OrbitControls, Stage, useGLTF, Html } from '@react-three/drei';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';

const useStyles = createStyles((theme) => ({
    wrapper: {
        paddingTop: 100,
        paddingBottom: 80,
        minHeight: '100vh',
        backgroundColor: '#050505',
        backgroundImage: 'radial-gradient(circle at center, #1a1a1a 0%, #000000 100%)',
        backgroundAttachment: 'fixed',
        color: theme.white,
        [theme.fn.smallerThan('sm')]: {
            paddingTop: 80,
            paddingBottom: 60,
        },
    },
    glassPanel: {
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        borderRadius: theme.radius.lg,
        padding: theme.spacing.xl,
        height: '100%',
        [theme.fn.smallerThan('sm')]: {
            padding: theme.spacing.md,
        },
    },
    modelContainer: {
        height: 500,
        width: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        borderRadius: theme.radius.lg,
        overflow: 'hidden',
        position: 'relative',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        [theme.fn.smallerThan('sm')]: {
            height: 350,
        },
    },
    backBtn: {
        position: 'absolute',
        top: 20,
        left: 20,
        zIndex: 10,
        backgroundColor: 'rgba(0,0,0,0.5)',
        color: theme.white,
        '&:hover': {
            backgroundColor: theme.colors.dark[6],
        }
    }
}));

const STLModel = ({ url }) => {
    const geometry = useLoader(STLLoader, url);
    return (
        <mesh geometry={geometry} scale={[0.1, 0.1, 0.1]}>
            <meshStandardMaterial color="#cccccc" />
        </mesh>
    );
};

const GLTFModel = ({ url }) => {
    const { scene } = useGLTF(url);
    return <primitive object={scene} />;
};

const ModelViewer = ({ url }) => {
    const isStl = url.toLowerCase().endsWith('.stl');
    if (isStl) return <STLModel url={url} />;
    return <GLTFModel url={url} />;
};

const ModelFallback = () => (
    <Html center>
        <Loader variant="dots" color="cyan" />
    </Html>
);

function ProductPage() {
    const { classes, theme } = useStyles();
    const [data, setData] = useState({});
    const location = useLocation();
    const bookId = location.pathname.split('/').pop(); // Safer split
    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            dispatch(setLoading(true));
            try {
                const res = await getBook(bookId);
                console.log("ProductPage Data Received:", res.data.data);
                setData(res.data.data);
                dispatch(setLoading(false));
            } catch (error) {
                toast.error('Error fetching data');
                dispatch(setLoading(false));
            }
        }
        fetchData();
    }, [bookId]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    function addItemToCart() {
        dispatch(addCartItem({ ...data, quantity: 1 }));
        toast.success("Added to Cart!");
    }

    function buyNow() {
        dispatch(addCartItem({ ...data, quantity: 1 }));
        navigate('/cart');
    }

    const [activeImageIndex, setActiveImageIndex] = useState(0);

    // Combine all media into a single array for the carousel
    // If model exists, it's index 0 (or handled separately).
    // Let's treat "Media Items" as:
    // 1. 3D Model (if exists) -> SPECIAL TYPE
    // 2. Cover Image
    // 3. Gallery Images

    const mediaItems = [];

    if (data.modelUrl) {
        mediaItems.push({ type: 'model', url: data.modelUrl, thumb: data.coverImage });
    }

    if (data.coverImage) {
        mediaItems.push({ type: 'image', url: data.coverImage, thumb: data.coverImage });
    }

    if (data.images && Array.isArray(data.images)) {
        data.images.forEach(img => {
            mediaItems.push({ type: 'image', url: img, thumb: img });
        });
    }

    // Handlers
    const handleNext = () => {
        setActiveImageIndex((prev) => (prev + 1) % mediaItems.length);
    };

    const handlePrev = () => {
        setActiveImageIndex((prev) => (prev - 1 + mediaItems.length) % mediaItems.length);
    };

    const activeItem = mediaItems[activeImageIndex];

    const getFullUrl = (url) => url?.startsWith('http') ? url : `https://backend.cdp3d.com${url}`;

    return (
        <div className={classes.wrapper}>
            <Container size="xl">
                <Button
                    variant="subtle"
                    color="gray"
                    leftIcon={<IconArrowLeft size={16} />}
                    mb="lg"
                    onClick={() => navigate('/store')}
                >
                    Back to Store
                </Button>

                <Grid gutter={40}>
                    {/* Visuals - Left Column */}
                    <Grid.Col md={7}>
                        <Stack spacing="md">
                            {/* Main Display */}
                            <Paper className={classes.glassPanel} p={0} style={{ overflow: 'hidden', position: 'relative' }}>
                                {activeItem?.type === 'model' ? (
                                    <div className={classes.modelContainer}>
                                        <div style={{ position: 'absolute', top: 15, left: 15, zIndex: 5 }}>
                                            <Badge variant="filled" color="dark">3D Preview</Badge>
                                        </div>
                                        <Canvas shadows dpr={[1, 2]} camera={{ fov: 50, position: [0, 0, 5] }}>
                                            <Suspense fallback={<ModelFallback />}>
                                                <Stage environment="city" intensity={0.5} contactShadow={false}>
                                                    <ModelViewer url={getFullUrl(activeItem.url)} />
                                                </Stage>
                                            </Suspense>
                                            <OrbitControls autoRotate />
                                        </Canvas>
                                        <Text size="xs" color="dimmed" align="center" style={{ position: 'absolute', bottom: 10, width: '100%' }}>
                                            Drag to Rotate • Scroll to Zoom
                                        </Text>
                                    </div>
                                ) : (
                                    <div style={{ position: 'relative', height: 500, width: '100%' }}>
                                        <Image
                                            src={getFullUrl(activeItem?.url)}
                                            height={500}
                                            fit="cover"
                                            withPlaceholder
                                        />
                                    </div>
                                )}

                                {/* Navigation Arrows (only if multiple items) */}
                                {mediaItems.length > 1 && (
                                    <>
                                        <ActionIcon
                                            variant="filled"
                                            color="dark"
                                            size="lg"
                                            radius="xl"
                                            style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', opacity: 0.7 }}
                                            onClick={handlePrev}
                                        >
                                            <IconArrowLeft size={20} />
                                        </ActionIcon>
                                        <ActionIcon
                                            variant="filled"
                                            color="dark"
                                            size="lg"
                                            radius="xl"
                                            style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', opacity: 0.7 }}
                                            onClick={handleNext}
                                        >
                                            <IconArrowLeft size={20} style={{ transform: 'rotate(180deg)' }} />
                                        </ActionIcon>
                                    </>
                                )}
                            </Paper>

                            {/* Thumbnail Strip */}
                            {mediaItems.length > 1 && (
                                <Group spacing="sm" style={{ overflowX: 'auto', paddingBottom: 5 }}>
                                    {mediaItems.map((item, index) => (
                                        <Paper
                                            key={index}
                                            p={2}
                                            style={{
                                                border: activeImageIndex === index ? '2px solid cyan' : '1px solid rgba(255,255,255,0.2)',
                                                borderRadius: 8,
                                                cursor: 'pointer',
                                                opacity: activeImageIndex === index ? 1 : 0.6,
                                                transition: 'all 0.2s ease',
                                                minWidth: 80
                                            }}
                                            onClick={() => setActiveImageIndex(index)}
                                        >
                                            {item.type === 'model' ? (
                                                <div style={{ width: 80, height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#111', borderRadius: 6 }}>
                                                    <Badge size="xs">3D</Badge>
                                                </div>
                                            ) : (
                                                <Image src={getFullUrl(item.thumb)} width={80} height={80} radius="sm" fit="cover" />
                                            )}
                                        </Paper>
                                    ))}
                                </Group>
                            )}
                        </Stack>
                    </Grid.Col>

                    {/* Details - Right Column */}
                    <Grid.Col md={5}>
                        <Stack spacing="lg" style={{ position: 'sticky', top: 120 }}>
                            <div>
                                <Badge size="lg" variant="gradient" gradient={{ from: 'cyan', to: 'indigo' }} mb="sm">
                                    In Stock
                                </Badge>
                                <Title order={1} style={{ fontSize: 42, color: theme.white, lineHeight: 1.1 }}>
                                    {data.title}
                                </Title>
                                <Text size="lg" color="dimmed" mt="xs">
                                    Designed by <Text span color="cyan" weight={500}>{data.author || 'CDP3D Designs'}</Text>
                                </Text>
                            </div>

                            <Divider color="rgba(255,255,255,0.1)" />

                            <Group align="end" spacing="xs">
                                <Text size={48} weight={800} color="white" style={{ lineHeight: 1 }}>
                                    ₹{data.currentPrice}
                                </Text>
                                {data.currentPrice < data.previousPrice && (
                                    <Text size="xl" color="dimmed" td="line-through" mb={6}>
                                        ₹{data.previousPrice}
                                    </Text>
                                )}
                                {data.currentPrice < data.previousPrice && (
                                    <Badge color="green" size="lg" mb={8} ml="sm">
                                        {Math.floor(((data.previousPrice - data.currentPrice) / data.previousPrice) * 100)}% SAVE
                                    </Badge>
                                )}
                            </Group>

                            <Group spacing="xs">
                                {data.tags?.map((tag, i) => (
                                    <Badge key={i} variant="outline" color="gray">
                                        {tag}
                                    </Badge>
                                ))}
                            </Group>

                            <Box mt="xl">
                                <Button
                                    fullWidth
                                    size="xl"
                                    variant="gradient"
                                    gradient={{ from: 'cyan', to: 'indigo' }}
                                    mb="md"
                                    onClick={addItemToCart}
                                    leftIcon={<IconShoppingCart size={22} />}
                                >
                                    Add to Cart
                                </Button>
                                <Button
                                    fullWidth
                                    size="lg"
                                    variant="outline"
                                    color="gray"
                                    onClick={buyNow}
                                    styles={{ root: { borderColor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' } } }}
                                >
                                    Buy Now
                                </Button>
                            </Box>

                            <Paper className={classes.glassPanel} p="lg" mt="lg">
                                <Title order={4} color="white" mb="sm">Description</Title>
                                <Text
                                    color="gray.3"
                                    dangerouslySetInnerHTML={{ __html: data.description }}
                                    sx={{
                                        'ul': { paddingLeft: 20 },
                                        'p': { marginBottom: 10 }
                                    }}
                                />
                            </Paper>
                        </Stack>
                    </Grid.Col>
                </Grid>
                <ReviewSection productId={bookId} />
            </Container>
        </div>
    );
}

export default ProductPage;